import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Input,
  Text,
  VStack,
  useToast,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

type LoginForm = {
  email: string;
};

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const [isVerifying, setIsVerifying] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { login } = useAuth();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>();

  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      // Scrub the token from the URL before verifying, so it can't linger in
      // the address bar or browser history if verification fails.
      window.history.replaceState({}, '', window.location.pathname);
      verifyToken(token);
    }
  }, [searchParams]);

  const verifyToken = async (token: string) => {
    setIsVerifying(true);
    try {
      const response = await api.auth.verifyToken(token);
      if (response.data.status === 'success') {
        // Drop anything cached for a previous partner in this tab
        queryClient.clear();
        login(response.data.token);
        const from = location.state?.from?.pathname || '/dashboard';
        navigate(from, { replace: true });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Invalid or expired login link',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const onSubmit = async (data: LoginForm) => {
    try {
      await api.auth.requestLogin(data.email);
      setEmailSent(true);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to send login link',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (isVerifying) {
    return (
      <Container maxW="lg" py={12}>
        <Alert status="info">
          <AlertIcon />
          Verifying your login link...
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="lg" py={12}>
      <Box
        bg="white"
        p={8}
        rounded="xl"
        shadow="lg"
        border="1px"
        borderColor="gray.100"
      >
        <VStack spacing={6}>
          <Text fontSize="2xl" fontWeight="bold" color="brand.500">
            Breez Partner Portal
          </Text>

          {emailSent ? (
            <Alert status="success">
              <AlertIcon />
              Check your email for the login link!
            </Alert>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
              <VStack spacing={4}>
                <FormControl isInvalid={!!errors.email}>
                  <FormLabel>Email Address</FormLabel>
                  <Input
                    type="email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address',
                      },
                    })}
                    placeholder="Enter your email"
                  />
                </FormControl>

                <Button
                  type="submit"
                  w="full"
                  size="lg"
                  isLoading={isSubmitting}
                >
                  Send Login Link
                </Button>
              </VStack>
            </form>
          )}
        </VStack>
      </Box>
    </Container>
  );
};

export default LoginPage; 